import { type WritableAtom } from "nanostores";
import type { ManageAPI } from "~/manage-api/types";
import { type PageParams, type PagePurpose, page_purpose_index } from "./ssr/router/page-purpose";
import type { Link } from "./components/field-utils";

export type ReverseRoute = [
  purpose: number,
  locale: string,
  api_friendly_url_path: string,
  path: string,
]

type ThinStore = Pick<ManageAPI.Store, "id" | "name" | "fieldOfBusinessId">;

export class ReverseRouter {

  readonly routes: ReverseRoute[];
  readonly purpose_specific: Map<string, string>;
  private locale: WritableAtom<string>;

  constructor(routes: ReverseRoute[], locale: WritableAtom<string>) {
    this.purpose_specific = new Map();
    this.routes = routes;
    this.locale = locale;
  }

  findPurposeRoute(page_purpose: PagePurpose, for_locale?: string): string {

    const locale = for_locale || this.locale.get();
    const key = `${locale}:${page_purpose}`;
    const path_cached = this.purpose_specific.get(key);

    if (path_cached) {
      return path_cached;
    }

    const index = page_purpose_index.indexOf(page_purpose);
    const [_p, _l, _a, path] = this.routes.find(
      ([purpose, page_locale]) => purpose == index && page_locale == locale
    ) || [];

    path && this.purpose_specific.set(key, path);
    return path || '';
  }

  /**
   * Converts an API friendly URL path to a locale specific path. It can also convert
   * a locale specific path to another locale's path in case locales aren't matching.
   * @param page_path - API friendly URL path or absolute URL
   * @param for_locale 
   * @param recursive - Internal use only 
   * @returns 
   */
  routePage(page_path: string, for_locale?: string, recursive?: boolean): string {
    // Absolute URL: pass through
    if (/^https?:/.test(page_path)) return page_path;

    let side_match;
    const locale = for_locale || this.locale.get();

    for (const [_, page_locale, api_friendly_url_path, locale_path] of this.routes) {
      // Direct match
      if (api_friendly_url_path == page_path && page_locale == locale) {
        return locale_path;
      }
      // Match via another locale
      else if (locale_path == page_path && page_locale != locale) {
        side_match = api_friendly_url_path;
      }
    }

    if (side_match && !recursive) {
      return this.routePage(side_match, locale, true);
    }

    // No match: return as is
    return page_path;
  }

  routePurpose(page_purpose: PagePurpose): string {
    return this.findPurposeRoute(page_purpose);
  }

  routeEvent(event: Pick<ManageAPI.Event, "id" | "title">, for_locale?: string) {
    return ReverseRouter.fillPathPattern(
      this.findPurposeRoute('event detail', for_locale),
      event.title,
      event.id
    )
  }

  routeStore(
    store: ThinStore, 
    scdata: ManageAPI.ShoppingCenterData,
    for_locale?: string
  ) {

    if (!store) {
      return '';
    }
    const fob_id = String(store.fieldOfBusinessId);
    const fob = scdata?.b2c_fobs?.find(x => x.id == fob_id || x.fob_ids.includes(Number(fob_id)));
    
    return ReverseRouter.fillPathPattern(
      this.findPurposeRoute('store detail', for_locale),
      store.name,
      store.id
    )
    .replace(/\[fob_slug\]/g, fob?.slug || fob_id)
  }

  /**
   * Generates a route for an offer publisher. If the offer has a URL, it will be 
   * returned as is otherwise a route to the store will be generated. Automatic 
   * generation uses API to redirect to the store page.
   * @param {ManageAPI.Offer} offer
   */
  routeOffer (offer: ManageAPI.Offer): string;
  routeOffer (
    offer: ManageAPI.Offer, 
    scdata?: ManageAPI.ShoppingCenterData,
    stores?: readonly ManageAPI.Store[],
    for_locale?: string
  ): string {

    if (!offer) {
      return '';
    }
    if (offer.url == 'automatic') {
      if (!scdata || !stores) {
        return ReverseRouter.fillPathPattern(
          `/api/link/store/[slug]/[entity_id]?lang=${this.locale.get()}`,
          offer.store?.name || '',
          offer.store?.id || 0
        );
      }
      const store = stores.find(x => x.id == offer.store?.id);
      return store ? this.routeStore(store, scdata) : '';
    }
    return /^(https?:)/.test(offer.url) ? offer.url : 'https://' + offer.url;
  }

  routeCustom(page_purpose: PagePurpose, title: string, id: number, for_locale?: string) {
    return ReverseRouter.fillPathPattern(
      this.findPurposeRoute(page_purpose, for_locale),
      title,
      id
    )
  }

  toJSON() {
    return this.routes;
  }

  /**
   * @param {{toString: () => string;}} text
   * @param {boolean?} as_id
   * @returns {string}
   * @see {https://gist.github.com/codeguy/6684588#gistcomment-3361909}
   */
  static slugify(text: { toString: () => string }, as_id: boolean | null = false): string {
    return (text || '')
      .toString()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .toLowerCase()
      .trim()
      .replace(/\s+/g, '-')
      .replace(/[^\w-]+/g, '')
      .replace(/--+/g, '-')
      .replace(/^\d/, (x) => (as_id ? `n${x[0]}` : x[0]));
  }

  static toHex(value: number): string {
    return value.toString(16);
  }

  static toInt(hex: string): string {
    if (hex.startsWith('$d')) return hex.substring(2);
    return String(parseInt(hex, 16));
  }

  static fillPathPattern(path_pattern: string, slug_src: string, id: number) {
    return path_pattern
      .replace(/\[slug\]/g, ReverseRouter.slugify(slug_src))
      .replace(/\[(entity_)id\]/g, ReverseRouter.toHex(id))
  }

  static decodeParams(params: PageParams) {
    const id = params?.entity_id || params?.id;
    if (id) {
      params.id = params.entity_id = ReverseRouter.toInt(id);
    }
    return params;
  }

}

/**
 * Returns a Link object with href and optional target `_blank` in case of external URL.
 * @param route - Either relative path or URL
 * @returns 
 */
export function asLink (route: string): Pick<Link, "href" | "target"> {
  return {
    href: route,
    target: /^https?:/.test(route) ? '_blank' : undefined
  }
}
